import React, { useCallback, useEffect, useRef, useState } from "react";

import { CopyOutlined } from "@ant-design/icons";
import { Button } from "antd";
import Title from "antd/es/typography/Title";
import { toast } from "utils/toast";

import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonInput,
  SkeletonText,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import { PaginationTable, ReferralInvitation } from "js/components/types";
import { Button as RetryButton } from "js/components/ui/button";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";

import ReferralInvitationsTable from "js/pages/Profile/Referrals/ReferralInvitationsTable";

import { getReferralCode, sendReferralEmail } from "../../../../api/accounts";
import { getReferralInvitationsPaginated } from "../../../../api/referrals";

const ReferralInvitations: React.FC = () => {
  const [data, setData] = useState<ReferralInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [referralCode, setReferralCode] = useState("");
  const [email, setEmail] = useState("");
  const emailInputRef = useRef<EmailInputRef>(null);

  const base_url = window.location.origin;
  const invite_url = `${base_url}/signup?referral_code=${referralCode}`;

  const fetchReferralResources = useCallback(async () => {
    setIsInitialLoading(true);
    setLoading(true);
    try {
      const [inviteResponse, referralResponse] = await Promise.all([
        getReferralInvitationsPaginated({
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: 0,
        }),
        getReferralCode(),
      ]);

      setData(inviteResponse);
      setPagination((prev) => {
        if (prev.total === inviteResponse.length) {
          return prev;
        }
        return {
          ...prev,
          total: inviteResponse.length,
        };
      });
      setReferralCode(referralResponse.referral_code);
    } catch (error) {
      console.error("Error fetching referral data:", error);
      setData([]);
    } finally {
      setLoading(false);
      setIsInitialLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  const handleCopyUrl = () => {
    const invite_message = `¡Hola! Quería contarte sobre Multiburo, una servicio genial para investigar prospectos o rentar de manera rápida y segura. Me ha ayudado mucho y creo que te encantará. Si decides probarla, usa mi código ${referralCode} al registrarte, o ingresa al siguiente enlace ${invite_url}. ¡Ambos obtendremos beneficios extra! 🚀 ¡Gracias y espero que la disfrutes! 👍`;
    navigator.clipboard.writeText(invite_message);
    toast.success("Mensaje copiado");
  };

  const handleInvite = async () => {
    // Validate email using EmailInput ref
    const isValid = await emailInputRef.current?.validate();
    if (!isValid) {
      return;
    }

    const requestData = { receiver_email: email };
    setLoading(true);
    sendReferralEmail(requestData)
      .then((response) => {
        setData([...data, response.data]);
        setEmail(""); // Clear email input after successful invite
      })
      .catch(({ response }) => {
        console.error("Error fetching data:", response);
        if (response.data.code == "3000") {
          toast.error("Ya existe un usuario registrado con ese correo");
        } else if (response.data.code == "3001") {
          toast.error("Ya enviaste una invitación a ese correo");
        } else {
          toast.error("Sucedió un error inesperado");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInviteSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handleInvite();
  };

  useEffect(() => {
    void fetchReferralResources();
  }, [fetchReferralResources]);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: isInitialLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void fetchReferralResources();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <RetryButton
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </RetryButton>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-8 w-52" />
    </div>
  );

  const actionsSkeleton = (
    <div className="flex flex-col gap-3 md:flex-row md:items-end">
      <div className="flex flex-col gap-1">
        <SkeletonText className="h-4 w-48" />
        <SkeletonInput className="h-8 w-full md:w-64" />
      </div>
      <SkeletonInput className="h-8 w-24" />
      <SkeletonBlock className="h-5 w-5 rounded" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4">
      <TableSkeleton columns={4} rows={6} />
    </div>
  );

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      actionsSkeleton={actionsSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-6"
    >
      <>
        <div>
          <Title level={2}>Invitaciones</Title>
        </div>

        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={handleInviteSubmit}
        >
          <div className="w-full sm:w-80">
            <EmailInput
              ref={emailInputRef}
              value={email}
              onChange={setEmail}
              label="Correo electrónico"
              placeholder="correo@ejemplo.com"
              required
              showIcon={false}
              showStatus={false}
              className="h-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button type="primary" htmlType="submit" loading={loading}>
              Invitar
            </Button>
            <CopyOutlined
              className="cursor-pointer text-lg hover:text-primary"
              onClick={handleCopyUrl}
            />
          </div>
        </form>

        <ReferralInvitationsTable
          data={data}
          setData={setData}
          pagination={pagination}
          setPagination={setPagination}
          loading={loading && !isInitialLoading}
        />
      </>
    </PageLoadingContainer>
  );
};

export default ReferralInvitations;
